/**
 * Property Media: all UI strings for the property media page and its sub-components.
 * Covers Photos, Floorplans, Videos, and Virtual Tours sections.
 *
 * Keep values in alphabetical order within each group.
 */

export const propertyMediaEn = {
  propertyMedia: {
    // Main Media page (Media.vue)
    page: {
      photosLabel: 'Photos [{count}]',
      floorplansLabel: 'Floorplans [{count}]',
      videosLabel: 'Videos [{count}]',
      virtualToursLabel: 'Virtual Tours [{count}]',
      listingScoresPhotos: 'Upload and enable 6 or more photos to improve your listing score.',
      listingScoresFloorplans: 'Upload and enable 1 or more floorplan(s) to improve your listing score.',
      importerActivityTitle: 'Importer Activity',
      importerActivityMessage: 'This section is managed by {source}. Changes made will be temporary.',
      websitesVisibility: 'Websites Visibility',
      listingsVisibility: 'Listings Visibility',
    },

    // Photos
    photos: {
      aiBanner: 'Level up your photos with Rentsync\'s new AI Enhanced photo editor! Select your photo and click "Enhance Photo" under Media Details to try it out.',
      promotionalBannerMain: 'Images that contain promotional content may not show within photo carousels on some rental listing sites.',
      promotionalBannerLink: 'Consider adding the image in the dedicated Promotions section instead -',
      enabledPhotos: 'Enabled Photos [{count}]',
      disabledPhotos: 'Disabled Photos [{count}]',
      dragToReorder: 'Drag and drop to reorder photos',
      addPhoto: 'Add Photo',
      disable: 'Disable',
      enable: 'Enable',
      uploadDragText: 'Drag here or',
      uploadBrowse: 'browse',
      uploadDragSuffix: 'to upload',
      uploadMaxSize: 'Maximum size limit per file: 32Mb',
      deleteTitle: 'Delete Image(s)',
      deleteCount: 'Number of image(s) that will be deleted: {count}',
      deleteConfirmText: 'Are you sure you want to delete the image(s)? This action cannot be undone.',
      deleteOne: 'Delete',
      deleteAll: 'Delete All',
      disableTitle: 'Are you sure you want to disable the following assigned photos?',
      invalidFileTitle: 'Invalid File Type',
      invalidFileText: 'Please upload a file with a .jpg, .jpeg, .png, or .gif extension.',
      uploadMaxSizeError: 'Maximum size limit is 32 MB.',
      uploadMaxSizeErrorTitle: 'Can\'t upload!',
      uploadSuccessTitle: 'Upload success!',
      uploadSuccessMessage: '{count} uploads completed!',
      enabledToast: '{count} photos enabled!',
      deletedToast: '{count} photos deleted!',
      disabledToast: '{count} photos disabled!',
      visibilityUpdated: 'Visibility updated!',
    },

    // Photo Details Panel
    photosDetails: {
      cardTitle: 'Media Details',
      editButton: 'Edit Media Details',
      selectPrompt: 'Select a file to preview here',
      labelFilename: 'Filename:',
      labelSourceWidth: 'Source Width:',
      labelSourceHeight: 'Source Height:',
      labelName: 'Name:',
      labelAltText: 'Alt Text:',
      labelTags: 'Tags:',
      tabDetails: 'Media Details',
      tabEnhancements: 'Enhancements',
      betaTag: 'BETA',
      enhancePhotoButton: 'Enhance Photo',
      saveEdits: 'Save Edits',
      unitLevelBanner: 'Changes made at the unit level will impact media at the property level.',
      formLabelName: 'Name',
      formLabelAltText: 'Alt Text',
      formLabelTags: 'Tags',
      editsToastMessage: 'Edits saved!',
      resetConfirmTitle: 'Are you sure?',
      resetConfirmText: 'Resetting this image will revert it back to how it looked when uploaded.',
      unsavedChangesTitle: 'You have unsaved changes!',
    },

    // Floorplans
    floorplans: {
      addFloorplan: 'Add Floorplan',
      tooltipLine1: 'Floorplans can be uploaded as a PDF, image file, or both. This is done because some websites may display the image file and link to the PDF.',
      tooltipLine2: 'For a better experience, drag and drop only images or only PDFs at a time, instead of both formats together.',
      uploadDragText: 'Drag here or',
      uploadBrowse: 'browse',
      uploadDragSuffix: 'to upload',
      uploadMaxSize: 'Maximum size limit per file: 32Mb',
      uploadSettingsTitle: 'Upload Settings',
      uploadFilesButton: 'Upload Files',
      uploadSuccessTitle: 'Upload success!',
      uploadSuccessMessage: 'Your floor plans have been uploaded successfully!',
      uploadFailTitle: 'Your floorplans were successfully uploaded except for the following:',
      invalidFileTitle: 'Invalid File Type',
      invalidFileText: 'Please upload a file with a .jpg, .jpeg, .png, or .gif extension.',
      deleteTitle: 'Delete Floorplan(s)',
      deleteCount: 'Number of floorplan(s) that will be deleted: {count}',
      deleteConfirmText: 'Are you sure you want to delete the floorplan(s)? This action cannot be undone.',
      deleteOne: 'Delete',
      deleteAll: 'Delete All',
      deletedToast: '{count} photos deleted!',
      deletedExceptToast: '{count} photos deleted! Except {failed} photos.',
      visibilityUpdated: 'Visibility updated!',
      addImageLabel: 'Add Image',
      addPdfLabel: 'Add PDF',
      chooseFile: 'Choose File',
      replaceFile: 'Replace File',
    },

    // Floorplan Details Panel
    floorplansDetails: {
      cardTitle: 'Media Details',
      editButton: 'Edit Media Details',
      selectPrompt: 'Select a file to preview here',
      previewImage: 'Preview Image',
      previewPdf: 'Preview PDF',
      labelImageName: 'Image Name:',
      labelPdfName: 'PDF Name:',
      labelSourceWidth: 'Source Width:',
      labelSourceHeight: 'Source Height:',
      labelName: 'Name:',
      labelTags: 'Tags:',
      popoutTitle: 'Floor Plans',
      saveEdits: 'Save Edits',
      noPreview: 'No Preview Available',
      formLabelName: 'Name',
      formLabelTags: 'Tags',
      formLabelImage: 'Image:',
      formLabelPdf: 'PDF:',
      replaceImage: 'Replace Image',
      attachImage: 'Attach Image',
      replacePdf: 'Replace PDF',
      attachPdf: 'Attach PDF',
      editsToastMessage: 'Edits saved!',
      unsavedChangesTitle: 'You have unsaved changes!',
    },

    // Videos
    videos: {
      addVideo: 'Add Video',
      deleteTitle: 'Delete Video(s)',
      deleteCount: 'Number of video(s) that will be deleted: {count}',
      deleteConfirmText: 'Are you sure you want to delete the video(s)? This action cannot be undone.',
      deleteOne: 'Delete',
      deleteAll: 'Delete All {count}',
      addErrorMessage: 'Failed to add video',
      addErrorTitle: 'Error Occurred!',
      deletedToast: '{count} videos deleted!',
      deletedExceptToast: '{count} videos deleted! except videos with title - {titles}',
      visibilityUpdated: 'Visibility updated!',
      editVideo: 'Edit Video',
      deleteDropdownItem: 'Delete Video',
    },

    // Video Details Panel
    videoDetails: {
      cardTitle: 'Media Details',
      editButton: 'Edit Media Details',
      selectPrompt: 'Select a video to preview here',
      labelTitle: 'Title',
      labelDescription: 'Description:',
      labelThumbnail: 'Thumbnail:',
      noThumbnail: 'No Thumbnail Available',
      labelEmbedCode: 'Embed Code:',
      labelTags: 'Tags:',
      popoutTitle: 'Video',
      saveEdits: 'Save Edits',
      unitLevelBanner: 'Changes made at the unit level will impact media at the property level.',
      formLabelTitle: 'Title',
      formLabelDescription: 'Description',
      formLabelEmbedCode: 'Embed Code:',
      formLabelTags: 'Tags',
      formLabelThumbnail: 'Thumbnail',
      replaceImage: 'Replace Image',
      addImage: 'Add Image',
      embedCodePlaceholder: 'Paste embed code here...',
      editsToastMessage: 'Edits saved!',
      unsavedChangesTitle: 'You have unsaved changes!',
    },

    // Virtual Tours
    virtualTours: {
      addVirtualTour: 'Add Virtual Tour',
      deleteTitle: 'Delete Virtual Tour(s)',
      deleteCount: 'Number of virtual tour(s) that will be deleted: {count}',
      deleteConfirmText: 'Are you sure you want to delete the virtual tour(s)? This action cannot be undone.',
      deleteOne: 'Delete',
      deleteAll: 'Delete All {count}',
      addErrorMessage: 'Failed to add virtual tour',
      addErrorTitle: 'Error Occurred!',
      deletedToast: '{count} virtual tours deleted!',
      deletedExceptToast: '{count} virtual tours deleted! except the ones with title - {titles}',
      editVirtualTour: 'Edit Virtual Tour',
      deleteDropdownItem: 'Delete Virtual Tour',
    },

    // Virtual Tour Details Panel
    virtualTourDetails: {
      cardTitle: 'Media Details',
      editButton: 'Edit Media Details',
      selectPrompt: 'Select a video to preview here',
      labelTitle: 'Title',
      labelDescription: 'Description:',
      labelThumbnail: 'Thumbnail:',
      noThumbnail: 'No Thumbnail Available',
      labelEmbedCode: 'Embed Code:',
      labelTags: 'Tags:',
      popoutTitle: 'Virtual Tours',
      saveEdits: 'Save Edits',
      unitLevelBanner: 'Changes made at the unit level will impact media at the property level.',
      formLabelTitle: 'Title',
      formLabelDescription: 'Description',
      formLabelEmbedCode: 'Embed Code:',
      formLabelTags: 'Tags',
      formLabelThumbnail: 'Thumbnail',
      replaceImage: 'Replace Image',
      addImage: 'Add Image',
      embedCodePlaceholder: 'Paste embed code here...',
      embedCodeError: 'Something is wrong with the Embed Code. Please check it and try again.',
      editsToastMessage: 'Edits saved!',
      unsavedChangesTitle: 'You have unsaved changes!',
    },

    // AI Photo Enhancement
    autoenhance: {
      originalPhotoLabel: 'Original Photo',
      revertToOriginal: 'Revert to original',
      enhancedPhotoLabel: 'Enhanced Photo',
      previewLargerTitle: 'Click to preview in larger window',
      generateFirstEnhancement: 'Generate First Enhancement',
      shadowBoxTitle: 'Enhanced Photo Preview',
      sectionEnhancements: 'Enhancements',
      labelEnhancementType: 'Enhancement Type',
      labelReplaceSky: 'Replace Sky',
      labelCloudType: 'Cloud Type',
      labelVerticalCorrection: 'Vertical Correction',
      labelContrastBoost: 'Contrast Boost',
      labelPrivacy: 'Privacy',
      privacyTooltip: 'This filter blurs sensitive information like licence plate numbers and faces from photos.',
      sectionRestaging: 'Restaging',
      labelTvBlackout: 'TV Blackout',
      tvBlackoutTooltip: 'Blacks out any switched-on TV screens so they aren\'t distracting.',
      labelFireplace: 'Fireplace',
      fireplaceTooltip: 'Lights a fire in any detected fireplaces to make the room feel warmer.',
      labelReflectionRemoval: 'Reflection Removal',
      reflectionRemovalTooltip: 'Removes the photographer\'s reflection from mirrors and other reflective surfaces.',
      previewChanges: 'Preview Changes',
      saveEnhancements: 'Save Enhancements',
      savingOverlay: 'Saving enhancement changes to your listing photo. This can take a few minutes...',
      revertingOverlay: 'Reverting enhancement changes to your listing photo. This can take a few minutes...',
      processingOverlay: 'Image enhancement is processing. This can take a few minutes...',
      noBundleError: 'Your account does not have the Rentals.ca Infinity Network enabled!',
      enhanceErrorToast: 'There was a problem generating your enhancement!',
      saveSuccessToast: 'Photo enhancement saved!',
      revertSuccessToast: 'Photo enhancement reverted!',
      remainingTooltip: 'The amount of enhancements remaining for this property this month. Saving a photo enhancement affects remaining amount until next month.',
      remainingCount: '{remaining} / {limit} remaining',
      selectOptions: {
        enhancementTypeWarmWhiteBalance: 'Warm White Balance',
        enhancementTypeNeutral: 'Neutral',
        cloudTypeClear: 'Clear',
        cloudTypeLow: 'Low',
        cloudTypeHigh: 'High',
        contrastNone: 'None',
        contrastLow: 'Low',
        contrastMedium: 'Medium',
        contrastHigh: 'High',
        yes: 'Yes',
        no: 'No',
      },
    },

    // Image Editor (RsPartialImageEditor.vue)
    imageEditor: {
      rotateLeft: 'Rotate Left',
      rotateRight: 'Rotate Right',
      cropImage: 'Crop Image',
      resetImage: 'Reset Image',
      cancelCrop: 'Cancel Crop',
      cropSelectedArea: 'Crop Selected Area',
      resetImageButton: 'Reset Image',
      saveChanges: 'Save Changes',
      noMediaAvailable: 'No Media Available',
    },
  },
} as const;

export const propertyMediaFr = {
  propertyMedia: {
    // Main Media page (Media.vue)
    page: {
      photosLabel: 'Photos [{count}]',
      floorplansLabel: 'Plans d\'étage [{count}]',
      videosLabel: 'Vidéos [{count}]',
      virtualToursLabel: 'Visites virtuelles [{count}]',
      listingScoresPhotos: 'Téléversez et activez 6 photos ou plus pour améliorer votre score d\'annonce.',
      listingScoresFloorplans: 'Téléversez et activez 1 plan d\'étage ou plus pour améliorer votre score d\'annonce.',
      importerActivityTitle: 'Activité de l\'importateur',
      importerActivityMessage: 'Cette section est gérée par {source}. Les modifications apportées seront temporaires.',
      websitesVisibility: 'Visibilité sur les sites web',
      listingsVisibility: 'Visibilité des annonces',
    },

    // Photos
    photos: {
      aiBanner: 'Améliorez vos photos avec le nouvel éditeur de photos IA de Rentsync ! Sélectionnez votre photo et cliquez sur « Améliorer la photo » sous Détails des médias pour l\'essayer.',
      promotionalBannerMain: 'Les images à caractère promotionnel pourraient ne pas s’afficher dans les carrousels de photos de certains sites d’annonces locatives.',
      promotionalBannerLink: 'Envisagez plutôt d’ajouter l’image dans la section dédiée aux promotions -',
      enabledPhotos: 'Photos activées [{count}]',
      disabledPhotos: 'Photos désactivées [{count}]',
      dragToReorder: 'Glisser-déposer pour réorganiser les photos',
      addPhoto: 'Ajouter une photo',
      disable: 'Désactiver',
      enable: 'Activer',
      uploadDragText: 'Glisser ici ou',
      uploadBrowse: 'parcourir',
      uploadDragSuffix: 'pour téléverser',
      uploadMaxSize: 'Taille maximale par fichier : 32 Mo',
      deleteTitle: 'Supprimer la/les image(s)',
      deleteCount: 'Nombre d\'image(s) qui seront supprimées : {count}',
      deleteConfirmText: 'Êtes-vous sûr de vouloir supprimer la/les image(s) ? Cette action est irréversible.',
      deleteOne: 'Supprimer',
      deleteAll: 'Tout supprimer',
      disableTitle: 'Êtes-vous sûr de vouloir désactiver les photos assignées suivantes ?',
      invalidFileTitle: 'Type de fichier invalide',
      invalidFileText: 'Veuillez téléverser un fichier avec l\'extension .jpg, .jpeg, .png ou .gif.',
      uploadMaxSizeError: 'La taille maximale est de 32 Mo.',
      uploadMaxSizeErrorTitle: 'Impossible de téléverser !',
      uploadSuccessTitle: 'Téléversement réussi !',
      uploadSuccessMessage: '{count} téléversement(s) terminé(s) !',
      enabledToast: '{count} photo(s) activée(s) !',
      deletedToast: '{count} photo(s) supprimée(s) !',
      disabledToast: '{count} photo(s) désactivée(s) !',
      visibilityUpdated: 'Visibilité mise à jour !',
    },

    // Photo Details Panel
    photosDetails: {
      cardTitle: 'Détails des médias',
      editButton: 'Modifier les détails des médias',
      selectPrompt: 'Sélectionnez un fichier pour l\'aperçu ici',
      labelFilename: 'Nom du fichier :',
      labelSourceWidth: 'Largeur source :',
      labelSourceHeight: 'Hauteur source :',
      labelName: 'Nom :',
      labelAltText: 'Texte alternatif :',
      labelTags: 'Étiquettes :',
      tabDetails: 'Détails des médias',
      tabEnhancements: 'Améliorations',
      betaTag: 'BÊTA',
      enhancePhotoButton: 'Améliorer la photo',
      saveEdits: 'Enregistrer les modifications',
      unitLevelBanner: 'Les modifications apportées au niveau de l\'unité auront un impact sur les médias au niveau de la propriété.',
      formLabelName: 'Nom',
      formLabelAltText: 'Texte alternatif',
      formLabelTags: 'Étiquettes',
      editsToastMessage: 'Modifications enregistrées !',
      resetConfirmTitle: 'Êtes-vous sûr ?',
      resetConfirmText: 'La réinitialisation de cette image la ramènera à son état au moment du téléversement.',
      unsavedChangesTitle: 'Vous avez des modifications non enregistrées !',
    },

    // Floorplans
    floorplans: {
      addFloorplan: 'Ajouter un plan d\'étage',
      tooltipLine1: 'Les plans d\'étage peuvent être téléversés en tant que PDF, fichier image ou les deux. C\'est parce que certains sites web peuvent afficher le fichier image et créer un lien vers le PDF.',
      tooltipLine2: 'Pour une meilleure expérience, glissez-déposez uniquement des images ou uniquement des PDF à la fois, plutôt que les deux formats ensemble.',
      uploadDragText: 'Glisser ici ou',
      uploadBrowse: 'parcourir',
      uploadDragSuffix: 'pour téléverser',
      uploadMaxSize: 'Taille maximale par fichier : 32 Mo',
      uploadSettingsTitle: 'Paramètres de téléversement',
      uploadFilesButton: 'Téléverser les fichiers',
      uploadSuccessTitle: 'Téléversement réussi !',
      uploadSuccessMessage: 'Vos plans d\'étage ont été téléversés avec succès !',
      uploadFailTitle: 'Vos plans d\'étage ont été téléversés avec succès, à l\'exception des suivants :',
      invalidFileTitle: 'Type de fichier invalide',
      invalidFileText: 'Veuillez téléverser un fichier avec l\'extension .jpg, .jpeg, .png ou .gif.',
      deleteTitle: 'Supprimer le/les plan(s) d\'étage',
      deleteCount: 'Nombre de plan(s) d\'étage qui seront supprimé(s) : {count}',
      deleteConfirmText: 'Êtes-vous sûr de vouloir supprimer le/les plan(s) d\'étage ? Cette action est irréversible.',
      deleteOne: 'Supprimer',
      deleteAll: 'Tout supprimer',
      deletedToast: '{count} photo(s) supprimée(s) !',
      deletedExceptToast: '{count} photo(s) supprimée(s) ! Sauf {failed} photo(s).',
      visibilityUpdated: 'Visibilité mise à jour !',
      addImageLabel: 'Ajouter une image',
      addPdfLabel: 'Ajouter un PDF',
      chooseFile: 'Choisir un fichier',
      replaceFile: 'Remplacer le fichier',
    },

    // Floorplan Details Panel
    floorplansDetails: {
      cardTitle: 'Détails des médias',
      editButton: 'Modifier les détails des médias',
      selectPrompt: 'Sélectionnez un fichier pour l\'aperçu ici',
      previewImage: 'Aperçu de l\'image',
      previewPdf: 'Aperçu du PDF',
      labelImageName: 'Nom de l\'image :',
      labelPdfName: 'Nom du PDF :',
      labelSourceWidth: 'Largeur source :',
      labelSourceHeight: 'Hauteur source :',
      labelName: 'Nom :',
      labelTags: 'Étiquettes :',
      popoutTitle: 'Plans d\'étage',
      saveEdits: 'Enregistrer les modifications',
      noPreview: 'Aperçu non disponible',
      formLabelName: 'Nom',
      formLabelTags: 'Étiquettes',
      formLabelImage: 'Image :',
      formLabelPdf: 'PDF :',
      replaceImage: 'Remplacer l\'image',
      attachImage: 'Joindre l\'image',
      replacePdf: 'Remplacer le PDF',
      attachPdf: 'Joindre le PDF',
      editsToastMessage: 'Modifications enregistrées !',
      unsavedChangesTitle: 'Vous avez des modifications non enregistrées !',
    },

    // Videos
    videos: {
      addVideo: 'Ajouter une vidéo',
      deleteTitle: 'Supprimer la/les vidéo(s)',
      deleteCount: 'Nombre de vidéo(s) qui seront supprimées : {count}',
      deleteConfirmText: 'Êtes-vous sûr de vouloir supprimer la/les vidéo(s) ? Cette action est irréversible.',
      deleteOne: 'Supprimer',
      deleteAll: 'Tout supprimer ({count})',
      addErrorMessage: 'Échec de l\'ajout de la vidéo',
      addErrorTitle: 'Une erreur s\'est produite !',
      deletedToast: '{count} vidéo(s) supprimée(s) !',
      deletedExceptToast: '{count} vidéo(s) supprimée(s) ! sauf les vidéos avec le titre — {titles}',
      visibilityUpdated: 'Visibilité mise à jour !',
      editVideo: 'Modifier la vidéo',
      deleteDropdownItem: 'Supprimer la vidéo',
    },

    // Video Details Panel
    videoDetails: {
      cardTitle: 'Détails des médias',
      editButton: 'Modifier les détails des médias',
      selectPrompt: 'Sélectionnez une vidéo pour l\'aperçu ici',
      labelTitle: 'Titre',
      labelDescription: 'Description :',
      labelThumbnail: 'Miniature :',
      noThumbnail: 'Aucune miniature disponible',
      labelEmbedCode: 'Code d\'intégration :',
      labelTags: 'Étiquettes :',
      popoutTitle: 'Vidéo',
      saveEdits: 'Enregistrer les modifications',
      unitLevelBanner: 'Les modifications apportées au niveau de l\'unité auront un impact sur les médias au niveau de la propriété.',
      formLabelTitle: 'Titre',
      formLabelDescription: 'Description',
      formLabelEmbedCode: 'Code d\'intégration :',
      formLabelTags: 'Étiquettes',
      formLabelThumbnail: 'Miniature',
      replaceImage: 'Remplacer l\'image',
      addImage: 'Ajouter une image',
      embedCodePlaceholder: 'Collez le code d\'intégration ici...',
      editsToastMessage: 'Modifications enregistrées !',
      unsavedChangesTitle: 'Vous avez des modifications non enregistrées !',
    },

    // Virtual Tours
    virtualTours: {
      addVirtualTour: 'Ajouter une visite virtuelle',
      deleteTitle: 'Supprimer la/les visite(s) virtuelle(s)',
      deleteCount: 'Nombre de visite(s) virtuelle(s) qui seront supprimées : {count}',
      deleteConfirmText: 'Êtes-vous sûr de vouloir supprimer la/les visite(s) virtuelle(s) ? Cette action est irréversible.',
      deleteOne: 'Supprimer',
      deleteAll: 'Tout supprimer ({count})',
      addErrorMessage: 'Échec de l\'ajout de la visite virtuelle',
      addErrorTitle: 'Une erreur s\'est produite !',
      deletedToast: '{count} visite(s) virtuelle(s) supprimée(s) !',
      deletedExceptToast: '{count} visite(s) virtuelle(s) supprimée(s) ! sauf celles avec le titre — {titles}',
      editVirtualTour: 'Modifier la visite virtuelle',
      deleteDropdownItem: 'Supprimer la visite virtuelle',
    },

    // Virtual Tour Details Panel
    virtualTourDetails: {
      cardTitle: 'Détails des médias',
      editButton: 'Modifier les détails des médias',
      selectPrompt: 'Sélectionnez une vidéo pour l\'aperçu ici',
      labelTitle: 'Titre',
      labelDescription: 'Description :',
      labelThumbnail: 'Miniature :',
      noThumbnail: 'Aucune miniature disponible',
      labelEmbedCode: 'Code d\'intégration :',
      labelTags: 'Étiquettes :',
      popoutTitle: 'Visites virtuelles',
      saveEdits: 'Enregistrer les modifications',
      unitLevelBanner: 'Les modifications apportées au niveau de l\'unité auront un impact sur les médias au niveau de la propriété.',
      formLabelTitle: 'Titre',
      formLabelDescription: 'Description',
      formLabelEmbedCode: 'Code d\'intégration :',
      formLabelTags: 'Étiquettes',
      formLabelThumbnail: 'Miniature',
      replaceImage: 'Remplacer l\'image',
      addImage: 'Ajouter une image',
      embedCodePlaceholder: 'Collez le code d\'intégration ici...',
      embedCodeError: 'Le code d\'intégration est invalide. Veuillez le vérifier et réessayer.',
      editsToastMessage: 'Modifications enregistrées !',
      unsavedChangesTitle: 'Vous avez des modifications non enregistrées !',
    },

    // AI Photo Enhancement
    autoenhance: {
      originalPhotoLabel: 'Photo originale',
      revertToOriginal: 'Revenir à l\'original',
      enhancedPhotoLabel: 'Photo améliorée',
      previewLargerTitle: 'Cliquez pour un aperçu en grand',
      generateFirstEnhancement: 'Générer la première amélioration',
      shadowBoxTitle: 'Aperçu de la photo améliorée',
      sectionEnhancements: 'Améliorations',
      labelEnhancementType: 'Type d\'amélioration',
      labelReplaceSky: 'Remplacer le ciel',
      labelCloudType: 'Type de nuage',
      labelVerticalCorrection: 'Correction verticale',
      labelContrastBoost: 'Amélioration du contraste',
      labelPrivacy: 'Confidentialité',
      privacyTooltip: 'Ce filtre floute les informations sensibles comme les plaques d\'immatriculation et les visages dans les photos.',
      sectionRestaging: 'Remise en scène',
      labelTvBlackout: 'Écran de télévision éteint',
      tvBlackoutTooltip: 'Éteint tout écran de télévision allumé afin d\'éviter les distractions.',
      labelFireplace: 'Foyer',
      fireplaceTooltip: 'Allume un feu dans les foyers détectés pour rendre la pièce plus chaleureuse.',
      labelReflectionRemoval: 'Suppression du reflet',
      reflectionRemovalTooltip: 'Supprime le reflet du photographe dans les miroirs et autres surfaces réfléchissantes.',
      previewChanges: 'Aperçu des modifications',
      saveEnhancements: 'Enregistrer les améliorations',
      savingOverlay: 'Enregistrement des modifications d\'amélioration de votre photo d\'annonce. Cela peut prendre quelques minutes…',
      revertingOverlay: 'Annulation des modifications d\'amélioration de votre photo d\'annonce. Cela peut prendre quelques minutes…',
      processingOverlay: 'Le traitement de l\'amélioration de l\'image est en cours. Cela peut prendre quelques minutes…',
      noBundleError: 'Votre compte n\'a pas le réseau Rentals.ca Infinity activé !',
      enhanceErrorToast: 'Un problème est survenu lors de la génération de votre amélioration !',
      saveSuccessToast: 'Amélioration de la photo enregistrée !',
      revertSuccessToast: 'Amélioration de la photo annulée !',
      remainingTooltip: 'Le nombre d\'améliorations restantes pour cette propriété ce mois-ci. Enregistrer une amélioration de photo affecte le nombre restant jusqu\'au mois prochain.',
      remainingCount: '{remaining} / {limit} restant(e)s',
      selectOptions: {
        enhancementTypeWarmWhiteBalance: 'Balance des blancs chaude',
        enhancementTypeNeutral: 'Neutre',
        cloudTypeClear: 'Dégagé',
        cloudTypeLow: 'Nuages bas',
        cloudTypeHigh: 'Nuages hauts',
        contrastNone: 'Aucun',
        contrastLow: 'Faible',
        contrastMedium: 'Moyen',
        contrastHigh: 'Élevé',
        yes: 'Oui',
        no: 'Non',
      },
    },

    // Image Editor (RsPartialImageEditor.vue)
    imageEditor: {
      rotateLeft: 'Pivoter à gauche',
      rotateRight: 'Pivoter à droite',
      cropImage: 'Recadrer l\'image',
      resetImage: 'Réinitialiser l\'image',
      cancelCrop: 'Annuler le recadrage',
      cropSelectedArea: 'Recadrer la zone sélectionnée',
      resetImageButton: 'Réinitialiser l\'image',
      saveChanges: 'Enregistrer les modifications',
      noMediaAvailable: 'Aucun média disponible',
    },
  },
} as const;
